const STORAGE_KEY = 'rationale:usual-picks:v1';
const DEFAULT_MAX_HISTORY = 10;
const FEATURE_FLAG_BODY_CLASS = 'usual-picks-enabled';
const getMaxHistory = () => {
    const configured = (window.theme || {}).usualPicksHistoryLimit;
    return Number(configured) > 0 ? Number(configured) : DEFAULT_MAX_HISTORY;
};
export const isEnabled = () => {
    return typeof document !== 'undefined' && !!document.body && document.body.classList.contains(FEATURE_FLAG_BODY_CLASS);
};
// Convert.com's variation script (document.body.classList.add(FEATURE_FLAG_BODY_CLASS)) runs
// async - timing isn't guaranteed relative to when a component first checks isEnabled(). This
// gives any caller a way to react if the flag flips on shortly after their own first check,
// via ONE shared observer (not one per caller/component).
let enabledWatchers = [];
let watcherStarted = false;
const notifyEnabled = () => {
    const toNotify = enabledWatchers;
    enabledWatchers = [];
    toNotify.forEach((callback) => callback());
};
const startEnabledWatcher = () => {
    if (watcherStarted || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
        return;
    }
    watcherStarted = true;
    const observer = new MutationObserver(() => {
        if (!isEnabled()) {
            return;
        }
        observer.disconnect();
        notifyEnabled();
    });
    if (document.body) {
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    else {
        document.addEventListener('DOMContentLoaded', () => {
            if (isEnabled()) {
                observer.disconnect();
                notifyEnabled();
                return;
            }
            if (document.body) {
                observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
            }
        });
    }
    // Convert can add the class between the first isEnabled() check and observe() -
    // re-check so we don't miss a mutation that already happened.
    if (isEnabled()) {
        observer.disconnect();
        notifyEnabled();
        return;
    }
    // Safety net: stop watching after 15s so this doesn't run forever on pages where the
    // flag never gets added (flag genuinely off, or no history).
    setTimeout(() => observer.disconnect(), 15000);
};
// Calls `callback` once the flag is on - immediately if it already is, otherwise the first
// time it flips on (within a 15s window after the first caller registers).
export const onEnabled = (callback) => {
    if (isEnabled()) {
        callback();
        return;
    }
    enabledWatchers.push(callback);
    startEnabledWatcher();
};
export const getHistory = () => {
    if (!isEnabled() || typeof localStorage === 'undefined') {
        return [];
    }
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    }
    catch (error) {
        return [];
    }
};
const setHistory = (history) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
    catch (error) {
        // localStorage can throw (Safari private mode, quota exceeded) - fail silently, tracking is best-effort
    }
};
export const record = (lineItems) => {
    // Deliberately NOT gated on isEnabled(): a visitor can add to cart before Convert.com has
    // bucketed them into the Variation on this page (direct PDP landing, page outside the
    // experiment's targeting, etc). Tracking needs to happen regardless, so the history is
    // already complete the moment they do land on a page where the flag gets turned on.
    // Only the user-visible behavior (carousel, badge, PLP sort) stays gated, via getHistory().
    if (typeof localStorage === 'undefined' || !Array.isArray(lineItems)) {
        return;
    }
    // Read the raw stored history directly (not the flag-gated getHistory()) so a disabled
    // flag never mutates it - re-enabling the flag later should see history untouched.
    let history = [];
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        history = Array.isArray(parsed) ? parsed : [];
    }
    catch (error) {
        history = [];
    }
    const now = Date.now();
    lineItems.forEach((item) => {
        if (!item || !item.handle || !item.product_id) {
            return;
        }
        const existingIndex = history.findIndex((pick) => pick.handle === item.handle);
        if (existingIndex !== -1) {
            const [existing] = history.splice(existingIndex, 1);
            history.unshift({
                handle: existing.handle,
                productId: existing.productId,
                addedAt: now,
                count: existing.count + 1,
            });
        }
        else {
            history.unshift({
                handle: item.handle,
                productId: item.product_id,
                addedAt: now,
                count: 1,
            });
        }
    });
    setHistory(history.slice(0, getMaxHistory()));
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('usual-picks-history-updated'));
    }
};
// Some add-to-cart paths (e.g. section-collection-ui.js's standalone ajaxAdd()) call
// fetch('/cart/add.js', ...) directly, bypassing cartUtil.ts's add(). Patching window.fetch
// here - rather than relying on any one caller to invoke record() - covers every add-to-cart
// path, present and future, with a single hook.
let fetchPatched = false;
const requestUrl = (input) => {
    if (typeof input === 'string')
        return input;
    if (input instanceof Request)
        return input.url;
    return String(input);
};
const requestMethod = (input, init) => {
    const method = (init === null || init === void 0 ? void 0 : init.method) || (input instanceof Request ? input.method : 'GET');
    return (method || 'GET').toUpperCase();
};
const recordFromResponse = (response) => {
    response
        .clone()
        .json()
        .then((data) => {
        const items = Array.isArray(data === null || data === void 0 ? void 0 : data.items) ? data.items : Array.isArray(data) ? data : null;
        if (items) {
            record(items);
        }
    })
        .catch(() => {
        // Response body isn't JSON, or already consumed - tracking is best-effort, fail silently
    });
};
const installFetchTracker = () => {
    if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return;
    }
    fetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
        const isCartAdd = requestMethod(input, init) === 'POST' && requestUrl(input).indexOf('/cart/add.js') !== -1;
        return originalFetch(input, init).then((response) => {
            if (isCartAdd && response.ok) {
                recordFromResponse(response);
            }
            return response;
        });
    };
};
installFetchTracker();
export const getHandles = () => getHistory().map((pick) => pick.handle);
export const getProductIds = () => getHistory().map((pick) => pick.productId);
export const hasProduct = (productId) => {
    return getProductIds().indexOf(productId) !== -1;
};
export default {
    isEnabled,
    onEnabled,
    record,
    getHistory,
    getHandles,
    getProductIds,
    hasProduct,
};
